import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { hasFramesByTimeRange } from '../utils/timelapseDB';
import { downloadImage, isShareSupported } from '../common/shareService';
import { getSubjectIcon } from '../common/subjectIconMap';
import '../css/HistoryDetailDialog.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryDetailDialog = ({
    session,
    onClose,
    onDeleteSession,
    onDeleteTimelapse,
    onGenerateTimelapse,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [framesAvailable, setFramesAvailable] = useState(false);
    const [canShare, setCanShare] = useState(false);

    const isExpired = () => {
        const createdAt = new Date(session.created_at || session.closed_at).getTime();
        return Date.now() - createdAt > 24 * 60 * 60 * 1000;
    };

    useEffect(() => {
        setCanShare(isShareSupported());
    }, []);

    useEffect(() => {
        if (!session) return;
        let cancelled = false;
        const check = async () => {
            try {
                if (!isExpired()) {
                    const openTime = new Date(session.opened_at).getTime();
                    const closeTime = new Date(session.closed_at).getTime();
                    const has = await hasFramesByTimeRange(openTime, closeTime);
                    if (!cancelled) setFramesAvailable(has);
                }
            } catch { /* silent */ }
        };
        check();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.session_id, session?.has_timelapse]);

    useEffect(() => {
        if (session?.has_timelapse && session?.video_path && videoRef.current) {
            videoRef.current.src = `${API_URL}/uploads/${session.video_path}`;
        }
    }, [session]);

    if (!session) return null;

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (h > 0) return `${h}${t('studyroom.hour')} ${m}${t('studyroom.min')}`;
        return `${m}${t('studyroom.min')}`;
    };

    const handleDownload = () => {
        if (!session.video_path) return;
        const ext = session.video_mimetype?.includes('mp4') ? 'mp4' : 'webm';
        const link = document.createElement('a');
        link.href = `${API_URL}/uploads/${session.video_path}`;
        link.download = `study-timelapse-${session.session_id}.${ext}`;
        link.click();
    };

    const handleShare = async () => {
        if (!session.video_path) return;
        try {
            const res = await fetch(`${API_URL}/uploads/${session.video_path}`);
            const blob = await res.blob();
            const ext = session.video_mimetype?.includes('mp4') ? 'mp4' : 'webm';
            const file = new File([blob], `study-timelapse.${ext}`, { type: blob.type });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: t('timelapse.title') });
            }
        } catch (err) {
            if (err.name !== 'AbortError') console.error('Share failed:', err);
        }
    };

    const expired = isExpired();
    const studySessions = session.studySessions || [];
    const maxSeconds = studySessions.length > 0
        ? Math.max(...studySessions.map(ss => ss.total_seconds), 1)
        : 1;

    return (
        <div className="hd-overlay" onClick={onClose}>
            <div className="hd-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="hd-header">
                    <h3 className="hd-title">{t('studyroom.history.detail')}</h3>
                    <button className="hd-close-x" onClick={onClose}>&times;</button>
                </div>

                {/* 시간 정보 */}
                <div className="hd-time-info">
                    <div className="hd-time-row">
                        <span className="hd-time-label">{formatDate(session.opened_at)}</span>
                        <span className="hd-time-range">
                            {formatTime(session.opened_at)} ~ {formatTime(session.closed_at)}
                        </span>
                        <span className="hd-duration">{formatDuration(session.duration_seconds)}</span>
                    </div>
                </div>

                {/* 학습 기록 */}
                {studySessions.length > 0 && (
                    <div className="hd-section">
                        <div className="hd-section-title">{t('studyroom.history.studySessions')}</div>
                        <div className="hd-subjects">
                            {studySessions.map((ss, idx) => (
                                <div key={idx} className="hd-subject-row">
                                    <div className="hd-subject-name">
                                        <span>{getSubjectIcon(ss.subject_name)}</span>
                                        {ss.subject_name}
                                    </div>
                                    <div className="hd-subject-bar-wrap">
                                        <div
                                            className="hd-subject-bar"
                                            style={{
                                                width: `${(ss.total_seconds / maxSeconds) * 100}%`,
                                                backgroundColor: ss.subject_color || '#4CAF50'
                                            }}
                                        />
                                        <span className="hd-subject-time">{formatDuration(ss.total_seconds)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 타임랩스 섹션 */}
                <div className="hd-section">
                    <div className="hd-section-title">{t('timelapse.title')}</div>

                    {session.has_timelapse ? (
                        <div className="hd-timelapse">
                            <video
                                ref={videoRef}
                                className="hd-video"
                                controls
                                playsInline
                                muted
                            />
                            <div className="hd-timelapse-actions">
                                <button className="hd-btn hd-btn-download" onClick={handleDownload}>
                                    {t('timelapse.download')}
                                </button>
                                {canShare && (
                                    <button className="hd-btn hd-btn-share" onClick={handleShare}>
                                        {t('share.share')}
                                    </button>
                                )}
                                {framesAvailable && !expired && (
                                    <button className="hd-btn hd-btn-regen" onClick={() => onGenerateTimelapse(session)}>
                                        {t('timelapse.regenerate')}
                                    </button>
                                )}
                                <button className="hd-btn hd-btn-delete-tl" onClick={() => onDeleteTimelapse(session.session_id)}>
                                    {t('studyroom.history.deleteTimelapse')}
                                </button>
                            </div>
                        </div>
                    ) : framesAvailable && !expired ? (
                        <div className="hd-timelapse-empty">
                            <button className="hd-btn hd-btn-generate" onClick={() => onGenerateTimelapse(session)}>
                                {t('studyroom.history.generateTimelapse')}
                            </button>
                        </div>
                    ) : (
                        <div className="hd-timelapse-none">
                            <p>{expired ? t('studyroom.history.framesExpired') : t('studyroom.history.noTimelapse')}</p>
                        </div>
                    )}
                </div>

                {/* 하단 액션 */}
                <div className="hd-footer">
                    <button className="hd-btn hd-btn-delete-session" onClick={() => onDeleteSession(session.session_id)}>
                        {t('studyroom.history.deleteSession')}
                    </button>
                    <button className="hd-btn hd-btn-close" onClick={onClose}>
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HistoryDetailDialog;
